import { BufferAttribute, BufferGeometry, LineBasicMaterial, LineSegments } from 'three';

const DROPS = 720;
const BOX = { x: 15, height: 15, ahead: 55, behind: 6 };
const FALL = 26;
const LENGTH = 0.9;

/** Rain streaks falling around the camera (a box of lines that follows it). Streaks slant as you drive into the rain. */
export class RainField {
  readonly object: LineSegments;
  private readonly positions: Float32Array;
  /** Each drop's position in the box: x, y (up), z (distance ahead of the camera, negative). */
  private readonly drops = new Float32Array(DROPS * 3);

  constructor() {
    this.positions = new Float32Array(DROPS * 6);
    for (let i = 0; i < DROPS; i += 1) {
      this.drops[i * 3] = (Math.random() * 2 - 1) * BOX.x;
      this.drops[i * 3 + 1] = Math.random() * BOX.height;
      this.drops[i * 3 + 2] = -BOX.ahead + Math.random() * (BOX.ahead + BOX.behind);
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    this.object = new LineSegments(geometry, new LineBasicMaterial({ color: '#d5e2ef', transparent: true, opacity: 0.62, depthWrite: false }));
    this.object.frustumCulled = false;
    this.object.visible = false;
  }

  set visible(on: boolean) {
    this.object.visible = on;
  }

  /** Moves the streaks down and towards the camera by the speed you're driving, and keeps the box around the camera. */
  update(dt: number, speed: number, cameraX: number, cameraY: number, cameraZ: number): void {
    this.object.position.set(cameraX, cameraY - 5, cameraZ);
    const slant = Math.min(1, speed / 40) * LENGTH * 1.8;
    for (let i = 0; i < DROPS; i += 1) {
      let x = this.drops[i * 3]!;
      let y = this.drops[i * 3 + 1]!;
      let z = this.drops[i * 3 + 2]!;
      y -= FALL * dt;
      z += speed * dt;
      if (y < 0 || z > BOX.behind) {
        x = (Math.random() * 2 - 1) * BOX.x;
        y = BOX.height * (0.7 + Math.random() * 0.3);
        z = -BOX.ahead + Math.random() * BOX.ahead * 0.35 - (z > BOX.behind ? 0 : Math.random() * BOX.ahead * 0.65);
      }
      this.drops[i * 3] = x;
      this.drops[i * 3 + 1] = y;
      this.drops[i * 3 + 2] = z;
      const p = i * 6;
      this.positions[p] = x;
      this.positions[p + 1] = y;
      this.positions[p + 2] = z;
      this.positions[p + 3] = x;
      this.positions[p + 4] = y + LENGTH;
      this.positions[p + 5] = z - slant;
    }
    (this.object.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
  }
}
